/* eslint-disable react/no-unescaped-entities */
import { motion } from "framer-motion";
import { Instagram, MapPin, ArrowUpRight, Star } from "lucide-react";
import { CENTERS } from "@/lib/content";

// Real Horsepower center photos provided by client.
const PLACEHOLDER_IMAGES = {
  beziers: "https://customer-assets.emergentagent.com/job_horsepower-bootcamp/artifacts/pw59ju5z_03A6A4AF-65FD-461C-9435-D31DDE88A8D3%20%282%29.PNG",
  montpellier: "https://customer-assets.emergentagent.com/job_horsepower-bootcamp/artifacts/9gi116vb_IMG_8480.JPG",
  perpignan: "https://customer-assets.emergentagent.com/job_horsepower-bootcamp/artifacts/ecc2y4lw_IMG_5166.jpg",
  marseille: "https://customer-assets.emergentagent.com/job_horsepower-bootcamp/artifacts/xarqor86_75dc8429-82a3-4860-9833-722b4b68a371.JPG",
};

// Cities for which the photo is the real provided one (shown in color, not desaturated).
const REAL_PHOTO = { beziers: true, montpellier: true, perpignan: true, marseille: true };

export default function Centers() {
  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#080808] border-y border-zinc-900" id="centres" data-testid="centers-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Nos centres</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Un réseau <span className="text-[#FFD60A]">national</span> en pleine expansion
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed">
              <span className="text-white font-semibold">4 centres physiques</span> en activité. Une vraie présence terrain, pas de la formation derrière un écran. Suivez nos coulisses sur Instagram.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
          {CENTERS.map((c, i) => {
            const key = c.city.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const img = PLACEHOLDER_IMAGES[key] || PLACEHOLDER_IMAGES.beziers;
            const isReal = REAL_PHOTO[key];
            return (
              <motion.a
                key={i}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group relative bg-[#0a0a0a] overflow-hidden block ${c.primary ? "ring-1 ring-[#FFD60A]/40" : ""}`}
                data-testid={`center-card-${key}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={img}
                    alt={`Centre Horsepower ${c.city}`}
                    className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${isReal ? "" : "grayscale group-hover:grayscale-0"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                  {c.primary && (
                    <div className="absolute top-3 left-3 px-2 py-1 bg-[#FFD60A] text-black font-bold text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                      <Star size={10} fill="currentColor" />
                      Centre principal
                    </div>
                  )}

                  <div className="absolute top-3 right-3 w-9 h-9 bg-black/70 backdrop-blur border border-white/20 group-hover:bg-[#FFD60A] group-hover:border-[#FFD60A] flex items-center justify-center transition-all">
                    <ArrowUpRight size={14} className="text-white group-hover:text-black transition-colors" />
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-2 text-[#FFD60A] mb-1">
                      <MapPin size={12} />
                      <span className="font-mono text-[10px] uppercase tracking-widest">{c.label}</span>
                    </div>
                    <h3 className="font-display text-3xl text-white uppercase leading-none">{c.city}</h3>
                    <div className="mt-3 flex items-center gap-2 text-zinc-300">
                      <Instagram size={12} />
                      <span className="font-mono text-xs">@{c.instagram}</span>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-4 border-t border-zinc-900">
                  <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3">{c.description}</p>
                </div>
              </motion.a>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-5 border border-zinc-800 bg-[#0a0a0a]">
          <div className="flex items-center gap-3">
            <Instagram size={20} className="text-[#FFD60A]" />
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Compte principal</div>
              <div className="text-white font-display text-xl tracking-wide">@horsepower_beziers</div>
            </div>
          </div>
          <a
            href="https://www.instagram.com/horsepower_beziers/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-5 py-3 bg-[#FFD60A] text-black font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all text-sm"
            data-testid="centers-main-insta-cta"
          >
            Voir nos coulisses
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
